import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Check, 
  Sparkles, 
  Brain, 
  TrendingUp, 
  FileText,
  Clock,
  Loader2,
  Crown,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { getSubscription, cancelSubscription, createCheckoutSession } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function Subscription() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await getSubscription();
        setSubscription(response.data.current);
        setPlans(response.data.plans);
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        toast.error('Failed to load subscription info');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSubscription();
  }, []);
  
  const handleUpgrade = async (tier) => {
    setIsUpdating(true);
    try {
      // Create package ID based on tier and billing period
      const packageId = `${tier}_${billingPeriod}`;
      
      // Get current origin for redirect URLs
      const originUrl = window.location.origin;
      
      const response = await createCheckoutSession(packageId, originUrl);
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsUpdating(false);
    }
  };
  
  const handleCancel = async () => {
    setIsUpdating(true);
    try {
      await cancelSubscription();
      const response = await getSubscription();
      setSubscription(response.data.current);
      toast.success('Subscription cancelled');
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setIsUpdating(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  const isTrialActive = subscription?.status === 'trial';
  const isPlusActive = subscription?.tier === 'plus' && subscription?.status === 'active';
  const isDeepActive = subscription?.tier === 'deep' && subscription?.status === 'active';
  const isFree = subscription?.tier === 'free' || subscription?.status === 'expired' || subscription?.status === 'cancelled';
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="subscription-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </header>
        
        <div className="px-6">
          {/* Title */}
          <div className="text-center mb-8">
            <img src={ONIRVA_ICON} alt="ONIRVA" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
            <h1 className="text-2xl font-serif font-bold mb-2">Choose Your Path</h1>
            <p className="text-muted-foreground text-sm">
              Deepen your dream intelligence journey
            </p>
          </div>
          
          {/* Trial Banner */}
          {isTrialActive && (
            <div className="glass-card p-4 mb-6 bg-gradient-to-r from-primary/20 to-violet-500/20 border-primary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold">Free Trial Active</h3>
                  <p className="text-xs text-muted-foreground">
                    {subscription.trial_days_remaining} days remaining • Full ONIRVA Plus access
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Current Status */}
          {!isTrialActive && (
            <div className="glass-card p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Plan</p>
                  <p className="font-bold">{subscription?.tier_name || 'Free'}</p>
                </div>
                {isFree && subscription?.analyses_remaining !== undefined && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Analyses remaining</p>
                    <p className="font-bold text-primary">{subscription.analyses_remaining}/3</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-6">
            <div className="glass-card p-1 inline-flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-xl text-sm transition-colors ${
                  billingPeriod === 'monthly' 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-2 rounded-xl text-sm transition-colors ${
                  billingPeriod === 'yearly' 
                    ? 'bg-primary text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly <span className="text-xs opacity-75">Save 33%</span>
              </button>
            </div>
          </div>
          
          {/* Plans */}
          <div className="space-y-4">
            {/* ONIRVA Plus */}
            <div className={`glass-card p-6 ${isPlusActive || isTrialActive ? 'border-primary/50' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <h2 className="font-serif font-bold text-lg">ONIRVA Plus</h2>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Unlimited AI dream analysis
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">
                    ${billingPeriod === 'monthly' ? '2.99' : '29.99'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    /{billingPeriod === 'monthly' ? 'month' : 'year'}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {plans?.plus?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {isPlusActive ? (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full"
                  disabled
                >
                  Current Plan
                </Button>
              ) : isTrialActive ? (
                <Button 
                  className="w-full rounded-full"
                  onClick={() => handleUpgrade('plus')}
                  disabled={isUpdating}
                >
                  {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe Now'}
                </Button>
              ) : isDeepActive ? (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full"
                  onClick={() => handleUpgrade('plus')}
                  disabled={isUpdating}
                >
                  Downgrade to Plus
                </Button>
              ) : (
                <Button 
                  className="w-full rounded-full glow-sm"
                  onClick={() => handleUpgrade('plus')}
                  disabled={isUpdating}
                >
                  {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Choose Plus'}
                </Button>
              )}
            </div>
            
            {/* ONIRVA Deep */}
            <div className={`glass-card p-6 ${isDeepActive ? 'border-primary/50' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <h2 className="font-serif font-bold text-lg">ONIRVA Deep</h2>
                    <span className="px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-400 text-xs">
                      Premium
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Long-term insight & reflection
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">
                    ${billingPeriod === 'monthly' ? '5.99' : '59.99'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    /{billingPeriod === 'monthly' ? 'month' : 'year'}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {plans?.deep?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {isDeepActive ? (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full"
                  disabled
                >
                  Current Plan
                </Button>
              ) : (
                <Button 
                  className="w-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                  onClick={() => handleUpgrade('deep')}
                  disabled={isUpdating}
                >
                  {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Choose Deep'}
                </Button>
              )}
            </div>
            
            {/* Free Tier Info */}
            <div className="glass-card p-6 opacity-75">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-serif font-bold text-lg mb-1">Free</h2>
                  <p className="text-sm text-muted-foreground">
                    Basic dream journaling
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">$0</p>
                  <p className="text-xs text-muted-foreground">forever</p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Unlimited dream journaling</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Voice capture & transcription</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>3 AI analyses per month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Secure & private</span>
                </li>
              </ul>
              
              {isFree ? (
                <Button 
                  variant="outline" 
                  className="w-full rounded-full"
                  disabled
                >
                  Current Plan
                </Button>
              ) : (isPlusActive || isDeepActive) && (
                <Button 
                  variant="ghost" 
                  className="w-full text-muted-foreground"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  Cancel subscription
                </Button>
              )}
            </div>
          </div>
          
          {/* Footer note */}
          <p className="text-center text-xs text-muted-foreground mt-8 px-4">
            Your journal is always accessible. Subscriptions support the continued development of ONIRVA. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
